import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import skillApis from '../../../apis/skills';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { academicYears } from '../../../constants/academicYears';
import { SnackbarSeverityType } from '../../../enums';
import { StyledButton } from '../../Exams/styled';
import {
  BoldSpan,
  CloneFormContainer,
  CloneFromTitle,
  CloneNoteText
} from '../styled';
import { CloneSkillsModalProps, DropdownChangeEvent } from '../types';
import useClassOptions from '../hooks/useClassOptions';

const CloneSkillsModal: React.FC<CloneSkillsModalProps> = ({
  open,
  onClose,
  academicYear,
  grade,
  division,
  allClasses,
  school,
  onSkillsCloned,
  showToast
}) => {
  const [cloneAcademicYear, setCloneAcademicYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cloneGrade, setCloneGrade] = useState('');
  const [cloneDivision, setCloneDivision] = useState('');

  const {
    gradeOptions: cloneGradeOptions,
    divisionOptions: cloneDivisionOptions
  } = useClassOptions(allClasses, cloneGrade);

  useEffect(() => {
    if (open) {
      setCloneAcademicYear('');
      setCloneGrade('');
      setCloneDivision('');
      setIsLoading(false);
    }
  }, [open]);

  const handleClone = async () => {
    setIsLoading(true);
    try {
      await skillApis.cloneSkills(school, {
        cloneFrom: {
          academicYear: cloneAcademicYear,
          grade: cloneGrade,
          division: cloneDivision
        },
        cloneTo: {
          academicYear,
          grade,
          division
        }
      });
      onSkillsCloned();
      setCloneAcademicYear('');
      setCloneGrade('');
      setCloneDivision('');
      showToast('Skills cloned successfully!', SnackbarSeverityType.success);
    } catch (error) {
      console.error('Error cloning skills:', error);
      showToast('Failed to clone skills.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcademicYearChange = (e: DropdownChangeEvent) => {
    setCloneAcademicYear(e.target.value as string);
    setCloneGrade('');
    setCloneDivision('');
  };

  const handleGradeChange = (e: DropdownChangeEvent) => {
    setCloneGrade(e.target.value as string);
    setCloneDivision('');
  };

  const handleDivisionChange = (e: DropdownChangeEvent) => {
    setCloneDivision(e.target.value as string);
  };

  const gradeOptions = cloneGradeOptions.map((grade) => ({
    value: grade,
    label: grade
  }));
  const divisionOptions = cloneDivisionOptions.map((division) => ({
    value: division,
    label: division
  }));

  const isCloneButtonDisabled =
    !academicYear ||
    !grade ||
    !division ||
    !cloneAcademicYear ||
    !cloneGrade ||
    !cloneDivision ||
    !school;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Clone Skills'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='contained'>
            Cancel
          </Button>
          <StyledButton
            onClick={handleClone}
            disabled={isLoading || isCloneButtonDisabled}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Clone'
            )}
          </StyledButton>
        </>
      }
    >
      <CloneNoteText variant='body2'>
        Note: You are cloning skills for <BoldSpan>{academicYear}</BoldSpan> of{' '}
        <BoldSpan>
          {grade}-{division}
        </BoldSpan>
      </CloneNoteText>
      <CloneFromTitle variant='h6'>Clone from</CloneFromTitle>
      <CloneFormContainer>
        <DropdownSelect
          label='Academic Year'
          placeholder='Select Academic Year'
          name='cloneAcademicYear'
          value={cloneAcademicYear}
          options={academicYears}
          onChange={handleAcademicYearChange}
        />
        <DropdownSelect
          label='Grade'
          placeholder='Select Grade'
          name='cloneGrade'
          value={cloneGrade}
          options={gradeOptions}
          onChange={handleGradeChange}
          loading={cloneGradeOptions.length === 0 && !!allClasses}
        />
        <DropdownSelect
          label='Division'
          placeholder='Select Division'
          name='cloneDivision'
          value={cloneDivision}
          options={divisionOptions}
          onChange={handleDivisionChange}
          loading={cloneDivisionOptions.length === 0 && !!cloneGrade}
        />
      </CloneFormContainer>
    </DialogModal>
  );
};

export default CloneSkillsModal;
